import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Select from '../components/SearchableSelect';
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import GroupsIcon from "@mui/icons-material/Groups";
import LogoutIcon from "@mui/icons-material/Logout";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import MenuIcon from "@mui/icons-material/Menu";
import RefreshIcon from "@mui/icons-material/Refresh";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getReservationEventsDailyBoard } from "../APIs/ReservationEventsAPI";
import { getAllTavoli } from "../APIs/TablesAPI";
import { getTenantSettings } from "../APIs/TenantSettingsAPI";
import { italianErrorMessage } from "../utils/italianErrorMessage";

const REFRESH_EVENTS = new Set([
  "reservation_event_created",
  "reservation_event_updated",
  "reservation_event_deleted",
]);

function getDateLocale(language) {
  if (language?.startsWith("sq")) return "sq-AL";
  if (language?.startsWith("en")) return "en-US";
  return "it-IT";
}

function formatTime(value, language) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--:--";
  return new Intl.DateTimeFormat(getDateLocale(language), {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatFullDate(date, language) {
  return new Intl.DateTimeFormat(getDateLocale(language), {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

function eventRecipient(event) {
  if (event?.company?.companyName) return event.company.companyName;
  const fullName = `${event?.client?.name || ""} ${event?.client?.surname || ""}`.trim();
  return fullName || event?.genericRecipientName || event?.quoteEmail || "-";
}

function eventPhone(event) {
  return event?.company?.phone || event?.client?.phone || "";
}

function dayTableLabels(day) {
  return (day?.tableLinks || []).map((link) => {
    const setup = link?.setup?.name ? ` - ${link.setup.name}` : "";
    const note = link?.note ? ` (${link.note})` : "";
    return `${link?.table?.name || `Sala ${link.tableId}`}${setup}${note}`;
  });
}

function usageTypeFromTableLink(link) {
  if (link?.usageType?.name) return link.usageType;
  const usageTypeId = Number(link?.usageTypeId);
  return Number.isInteger(usageTypeId) && usageTypeId > 0
    ? { id: usageTypeId, name: `#${usageTypeId}` }
    : null;
}

function dayUsageTypeNames(day) {
  const names = (day?.tableLinks || [])
    .map((link) => usageTypeFromTableLink(link)?.name)
    .filter(Boolean);
  return [...new Set(names)];
}

function normalizePositiveId(value) {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
}

function dayMatchesTable(day, selectedTableId) {
  const tableId = normalizePositiveId(selectedTableId);
  if (!tableId) return true;
  return (day?.tableLinks || []).some((link) => {
    const linkedTableId = normalizePositiveId(link?.table?.id ?? link?.tableId);
    return linkedTableId === tableId;
  });
}

function normalizeOperationItem(item, type, fallbackTitle) {
  const stableId = item.id || item.packageId || item.productId || item.description || fallbackTitle;
  const baseTitle = item.description || item.package?.name || item.product?.name || item.name || fallbackTitle;
  const variantName = item.variant?.name;
  return {
    id: `${type}-${stableId}`,
    title: variantName && !String(baseTitle).includes(variantName)
      ? `${baseTitle} - ${variantName}`
      : baseTitle,
    department: item.department?.name || item.departmentName || "",
    note: item.note || "",
    quantity: item.quantity,
    orario: item.orario || item.serviceTime || "",
  };
}

function operationRows(day) {
  const packages = [...(day?.packages || []), ...(day?.packageItems || [])]
    .map((item) => normalizeOperationItem(item, "pkg", "Pacchetto"));
  const services = [...(day?.services || []), ...(day?.serviceItems || [])]
    .map((item) => normalizeOperationItem(item, "svc", "Servizio"));
  const customRows = [...(day?.customRows || []), ...(day?.customItems || [])]
    .map((item) => normalizeOperationItem(item, "row", "Attivita"));
  return [...packages, ...services, ...customRows]
    .filter((row) => row.title || row.department || row.note)
    .sort((a, b) => {
      const aTime = timeToSortValue(a.orario);
      const bTime = timeToSortValue(b.orario);
      if (aTime !== bTime) return aTime - bTime;
      return String(a.title || "").localeCompare(String(b.title || ""), "it", { sensitivity: "base", numeric: true });
    });
}

function timeToSortValue(value) {
  const match = String(value || "").match(/(\d{1,2})[:.](\d{2})/);
  if (!match) return Number.POSITIVE_INFINITY;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return Number.POSITIVE_INFINITY;
  return hours * 60 + minutes;
}

function AutoScrollViewport({ children, itemCount = 0 }) {
  const shouldAnimate = itemCount > 3;
  const duration = `${Math.max(18, itemCount * 5)}s`;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        pr: 0.3,
        WebkitMaskImage: "linear-gradient(to bottom, transparent 0, black 14px, black calc(100% - 14px), transparent 100%)",
        maskImage: "linear-gradient(to bottom, transparent 0, black 14px, black calc(100% - 14px), transparent 100%)",
      }}
    >
      <Box
        sx={{
          "@keyframes monitorServiceScroll": {
            "0%": { transform: "translateY(0)" },
            "100%": { transform: "translateY(-50%)" },
          },
          animation: shouldAnimate ? `monitorServiceScroll ${duration} linear infinite` : "none",
          willChange: shouldAnimate ? "transform" : "auto",
        }}
      >
        <Stack spacing={0.8} sx={{ py: 0.25 }}>
          {children}
        </Stack>
        {shouldAnimate && (
          <Stack spacing={0.8} sx={{ py: 0.25 }} aria-hidden="true">
            {children}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function dayPhase(day, event, now) {
  if (event?.status === "COMPLETED") return "COMPLETED";
  if (event?.status === "CHECKED_IN") return "CHECKED_IN";
  const end = new Date(day?.endDate).getTime();
  const current = now.getTime();
  if (Number.isFinite(end) && current > end) return "ENDED";
  return event?.status || "";
}

function phaseLabel(phase, t) {
  if (phase === "CHECKED_IN") return t("checkIn", { defaultValue: "Check-in" });
  if (phase === "COMPLETED") return t("eventoCompletato", { defaultValue: "Evento completato" });
  if (phase === "ENDED") return t("monitorEventi.ended", { defaultValue: "Terminato" });
  if (phase === "TO_CONFIRM") return t("daconfermare", { defaultValue: "Da confermare" });
  if (phase === "CONFIRMED") return t("confermata", { defaultValue: "Confermata" });
  return phase || "-";
}

function practiceTypeLabel(value, t) {
  const normalized = String(value || "").toUpperCase();
  if (normalized === "PRENOTAZIONE") return t("reservation", { defaultValue: "Reservation" });
  if (normalized === "PREVENTIVO") return t("preventivo", { defaultValue: "Quote" });
  if (normalized === "EVENTO") return t("event", { defaultValue: "Event" });
  return value;
}

export default function CongressTodayMonitorPage() {
  const tenantType = String(localStorage.getItem("tenantType") || "").toUpperCase();
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const language = i18n.language;

  const [tables, setTables] = useState([]);
  const [dailyBoardDays, setDailyBoardDays] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [now, setNow] = useState(() => new Date());
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
  const [eventColors, setEventColors] = useState({
    inProgress: "#F59E0B",
    completed: "#16A34A",
  });
  const loadRequestRef = useRef(0);

  const loadData = useCallback(async () => {
    if (!token || !tenant) return;
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    setLoading(true);
    setError("");
    try {
      const [tableData, eventData, settingsData] = await Promise.all([
        getAllTavoli(token, tenant, { noCache: true }),
        getReservationEventsDailyBoard(token, tenant),
        getTenantSettings(token, tenant),
      ]);
      if (requestId !== loadRequestRef.current) return;
      setTables(Array.isArray(tableData) ? tableData : []);
      setDailyBoardDays(Array.isArray(eventData) ? eventData.filter((day) => dayMatchesTable(day, selectedTableId)) : []);
      setEventColors({
        inProgress: settingsData?.eventInProgressColor || "#F59E0B",
        completed: settingsData?.eventCompletedColor || "#16A34A",
      });
      setLastUpdatedAt(new Date());
    } catch (err) {
      if (requestId !== loadRequestRef.current) return;
      setError(italianErrorMessage(err, t("monitorEventi.loadError")));
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  }, [selectedTableId, t, tenant, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
      loadData();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  useEffect(() => {
    let timer = null;
    const bc = new BroadcastChannel("ws-events");
    const handler = ({ data }) => {
      if (!REFRESH_EVENTS.has(data?.event)) return;
      clearTimeout(timer);
      timer = setTimeout(loadData, 220);
    };
    bc.addEventListener("message", handler);
    return () => {
      clearTimeout(timer);
      bc.removeEventListener("message", handler);
      bc.close();
    };
  }, [loadData]);

  useEffect(() => {
    const handler = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  useEffect(() => {
    if (!controlsVisible) return undefined;
    const timer = setTimeout(() => setControlsVisible(false), 2200);
    return () => clearTimeout(timer);
  }, [controlsVisible]);

  const sortedTables = useMemo(() => (
    [...tables]
      .sort((a, b) => {
        const roomDiff = Number(a.roomId || 0) - Number(b.roomId || 0);
        if (roomDiff !== 0) return roomDiff;
        return String(a.name || "").localeCompare(String(b.name || ""), "it", { sensitivity: "base", numeric: true });
      })
  ), [tables]);

  const monitorRows = useMemo(() => {
    return dailyBoardDays
      .map((day) => ({ event: day.event || {}, day }))
      .sort((a, b) => new Date(a.day.startDate) - new Date(b.day.startDate));
  }, [dailyBoardDays]);

  const maxVisibleRows = 8;
  const visibleRows = monitorRows.slice(0, maxVisibleRows);
  const hiddenRows = Math.max(0, monitorRows.length - visibleRows.length);
  const currentDateLabel = formatFullDate(now, language);
  const eventCount = visibleRows.length;
  const useTwoColumnBoard = eventCount >= 3;
  const compactBoard = eventCount >= 5;

  const requestFullscreen = useCallback(async () => {
    const element = document.documentElement;
    if (document.fullscreenElement || !element?.requestFullscreen) return;
    try {
      await element.requestFullscreen();
    } catch (_) {
      setControlsVisible(true);
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    if (!document.fullscreenElement || !document.exitFullscreen) return;
    try {
      await document.exitFullscreen();
    } catch (_) {
      // Browser denied the fullscreen exit request; navigation remains available.
    }
  }, []);

  const showControls = useCallback(() => {
    setControlsVisible(true);
  }, []);

  const handlePageClick = () => {
    showControls();
    if (!document.fullscreenElement) requestFullscreen();
  };

  const leaveMonitor = async () => {
    await exitFullscreen();
    navigate("/prenotazione");
  };

  const phaseSx = (phase) => {
    if (phase === "CHECKED_IN") return {
      bgcolor: alpha(eventColors.inProgress, theme.palette.mode === "dark" ? 0.28 : 0.18),
      color: theme.palette.mode === "dark" ? "#fff" : "#7A4300",
      borderColor: alpha(eventColors.inProgress, 0.72),
    };
    if (phase === "COMPLETED" || phase === "ENDED") return {
      bgcolor: alpha(eventColors.completed, theme.palette.mode === "dark" ? 0.26 : 0.16),
      color: theme.palette.mode === "dark" ? "#fff" : "#0F5132",
      borderColor: alpha(eventColors.completed, 0.65),
    };
    if (phase === "TO_CONFIRM") return {
      bgcolor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.24 : 0.12),
      color: theme.palette.info.dark,
      borderColor: alpha(theme.palette.info.main, 0.45),
    };
    return {
      bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.22 : 0.12),
      color: theme.palette.success.dark,
      borderColor: alpha(theme.palette.success.main, 0.45),
    };
  };

  if (tenantType !== "SALE_CONGRESSI") {
    return <Navigate to="/home" replace />;
  }

  return (
    <Box
      onMouseMove={showControls}
      onPointerDown={showControls}
      onClick={handlePageClick}
      sx={{
        height: "100dvh",
        width: "100vw",
        overflow: "hidden",
        bgcolor: theme.palette.mode === "dark" ? "#071016" : "#F4F7F8",
        color: "text.primary",
        display: "grid",
        gridTemplateRows: "auto 1fr",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: { xs: 1.25, md: 1.75 },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.92 : 0.98),
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <EventAvailableIcon color="primary" sx={{ fontSize: { xs: 30, lg: 36 } }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: 22, lg: 30 }, fontWeight: 950, lineHeight: 1.05 }} noWrap>
              {t("monitorEventi.title", { defaultValue: "Eventi di oggi" })}
            </Typography>
            <Typography sx={{ display: "none" }} noWrap>
              {currentDateLabel} · {formatTime(now, language)}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.35 }}>
              <Typography sx={{ fontSize: { xs: 13, lg: 16 }, color: "text.secondary" }} noWrap>
                {currentDateLabel} | {formatTime(now, language)}
              </Typography>
              <Box
                sx={{
                  px: 1,
                  py: 0.35,
                  borderRadius: 99,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.18 : 0.1),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.28)}`,
                  color: theme.palette.mode === "dark" ? "#DDF8E7" : "#17613A",
                  fontSize: { xs: 11, lg: 12 },
                  fontWeight: 900,
                  textTransform: "uppercase",
                  lineHeight: 1,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: loading ? theme.palette.warning.main : theme.palette.success.main,
                    boxShadow: `0 0 0 4px ${alpha(loading ? theme.palette.warning.main : theme.palette.success.main, 0.16)}`,
                    "@keyframes livePulse": {
                      "0%": { transform: "scale(1)", opacity: 1 },
                      "70%": { transform: "scale(1.45)", opacity: 0.55 },
                      "100%": { transform: "scale(1)", opacity: 1 },
                    },
                    animation: "livePulse 1.8s ease-in-out infinite",
                  }}
                />
                {loading
                  ? t("loading", { defaultValue: "Caricamento..." })
                  : t("monitorEventi.realtimeShort", { defaultValue: "Live" })}
                {lastUpdatedAt ? ` ${formatTime(lastUpdatedAt, language)}` : ""}
              </Box>
            </Stack>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          <FormControl size="small" sx={{ minWidth: { xs: 190, md: 260 } }}>
            <InputLabel id="monitor-room-filter-label">{t("sala", { defaultValue: "Sala" })}</InputLabel>
            <Select
              labelId="monitor-room-filter-label"
              value={selectedTableId}
              label={t("sala", { defaultValue: "Sala" })}
              onChange={(event) => setSelectedTableId(event.target.value)}
              onClick={(event) => event.stopPropagation()}
              sx={{ bgcolor: "background.paper" }}
            >
              <MenuItem value="">{t("tutte", { defaultValue: "Tutte" })}</MenuItem>
              {sortedTables.map((table) => (
                <MenuItem key={table.id} value={table.id}>
                  {table.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Chip
            icon={<MeetingRoomIcon />}
            label={`${monitorRows.length} ${t("monitorEventi.events", { defaultValue: "eventi" })}`}
            sx={{ height: 40, fontWeight: 800 }}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          minHeight: 0,
          p: { xs: 1.5, md: 2.5 },
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          bgcolor: theme.palette.mode === "dark" ? "#061016" : "#EEF3F4",
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: useTwoColumnBoard ? "repeat(2, minmax(0, 1fr))" : "1fr" },
            gridAutoRows: eventCount === 0
              ? "1fr"
              : eventCount <= 2
              ? { xs: "minmax(250px, 330px)", lg: "minmax(300px, 370px)" }
              : "minmax(0, 1fr)",
            gridTemplateRows: !useTwoColumnBoard && eventCount > 2 ? `repeat(${eventCount}, minmax(0, 1fr))` : undefined,
            alignContent: eventCount ? "start" : "stretch",
            gap: { xs: 1.25, md: 2 },
            overflow: "hidden",
          }}
        >
          {loading && !visibleRows.length ? (
            <Paper sx={{ display: "grid", placeItems: "center", borderRadius: 2 }}>
              <CircularProgress />
            </Paper>
          ) : visibleRows.length ? (
            visibleRows.map(({ event, day }) => {
              const phase = dayPhase(day, event, now);
              const rows = operationRows(day);
              const roomLabels = dayTableLabels(day);
              const usageTypeNames = dayUsageTypeNames(day);
              const phone = eventPhone(event);
              const accentColor = phase === "CHECKED_IN"
                ? eventColors.inProgress
                : phase === "COMPLETED" || phase === "ENDED"
                  ? eventColors.completed
                  : theme.palette.primary.main;
              return (
                <Paper
                  key={`${event.id}-${day.id}`}
                  sx={{
                    height: "100%",
                    minHeight: 0,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.11 : 0.45)}`,
                    borderLeft: `7px solid ${accentColor}`,
                    display: "grid",
                    gridTemplateRows: "auto 1fr",
                    bgcolor: theme.palette.mode === "dark" ? "#172126" : "#FFFFFF",
                    boxShadow: `0 18px 44px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.32 : 0.1)}`,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      px: { xs: 1.25, lg: 1.75 },
                      py: { xs: 0.85, lg: 1.1 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                      bgcolor: alpha(accentColor, theme.palette.mode === "dark" ? 0.12 : 0.08),
                      minWidth: 0,
                    }}
                  >
                    <Chip
                      size="small"
                      label={phaseLabel(phase, t)}
                      sx={{ maxWidth: "100%", fontWeight: 900, border: "1px solid", ...phaseSx(phase) }}
                    />
                    <Typography sx={{ color: "text.secondary", fontSize: { xs: 12, lg: 14 }, fontWeight: 850 }} noWrap>
                      {roomLabels.join(", ") || t("sala", { defaultValue: "Sala" })}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      minHeight: 0,
                      p: { xs: 1.25, lg: compactBoard ? 1.35 : 1.75 },
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "118px minmax(0, 1fr)",
                        md: "120px minmax(180px, 0.8fr) minmax(260px, 1.1fr)",
                        lg: useTwoColumnBoard
                          ? "110px minmax(150px, 0.8fr) minmax(180px, 1.1fr)"
                          : "180px minmax(0, 1fr) minmax(360px, 0.9fr)",
                      },
                      gridTemplateRows: { xs: "auto auto", md: "1fr" },
                      gap: { xs: 1.25, lg: compactBoard ? 1.25 : 1.75 },
                      alignItems: "stretch",
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 0,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.22 : 0.04),
                        border: `1px solid ${alpha(theme.palette.divider, 0.65)}`,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        px: { xs: 1, lg: 1.35 },
                      }}
                    >
                      <Typography sx={{ fontSize: { xs: 25, lg: compactBoard ? 28 : 38 }, fontWeight: 950, lineHeight: 0.95 }}>
                        {formatTime(day.startDate, language)}
                      </Typography>
                      <Typography sx={{ mt: 0.65, fontSize: { xs: 14, lg: 18 }, fontWeight: 900, color: "text.secondary" }}>
                        {formatTime(day.endDate, language)}
                      </Typography>
                    </Box>

                    <Stack spacing={compactBoard ? 0.8 : 1.05} sx={{ minWidth: 0, justifyContent: "center" }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 27, lg: compactBoard ? 30 : 40 },
                          fontWeight: 950,
                          lineHeight: 1.06,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: compactBoard ? 1 : 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {event.eventName}
                      </Typography>
                      <Typography sx={{ fontSize: { xs: 18, lg: 25 }, color: "text.secondary", fontWeight: 800 }} noWrap>
                        {eventRecipient(event)}
                      </Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        {event.practiceType && (
                          <Chip size="small" label={practiceTypeLabel(event.practiceType, t)} sx={{ height: 30, fontSize: 15, fontWeight: 900 }} />
                        )}
                        {usageTypeNames.length > 0 && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={usageTypeNames.join(", ")}
                            sx={{ height: 30, fontSize: 15, fontWeight: 900 }}
                          />
                        )}
                        {phone && (
                          <Typography sx={{ fontSize: { xs: 16, lg: 21 }, color: "text.secondary", fontWeight: 900 }} noWrap>
                            {phone}
                          </Typography>
                        )}
                      </Stack>
                      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <GroupsIcon sx={{ fontSize: 26 }} color="action" />
                          <Typography sx={{ fontSize: { xs: 18, lg: 24 }, fontWeight: 950 }}>{day.guests || 0}</Typography>
                        </Stack>
                        {day.note && (
                          <>
                            <Divider flexItem orientation="vertical" />
                            <Typography sx={{ fontSize: { xs: 15, lg: 20 }, color: "text.secondary", fontWeight: 750 }} noWrap>
                              {day.note}
                            </Typography>
                          </>
                        )}
                      </Stack>
                    </Stack>

                    <Box
                      sx={{
                        gridColumn: { xs: "1 / -1", md: "auto" },
                        minWidth: 0,
                        minHeight: { xs: 110, md: 0 },
                        borderRadius: 1.5,
                        border: `1px solid ${alpha(theme.palette.divider, 0.65)}`,
                        bgcolor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.16 : 0.025),
                        p: { xs: 1, lg: compactBoard ? 1 : 1.25 },
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Stack direction="row" spacing={0.9} alignItems="center" sx={{ mb: 1 }}>
                        <RoomServiceIcon sx={{ fontSize: 26, color: accentColor }} />
                        <Typography sx={{ fontSize: { xs: 17, lg: 22 }, fontWeight: 950, textTransform: "uppercase" }}>
                          {t("monitorEventi.services", { defaultValue: "Services" })}
                        </Typography>
                      </Stack>
                      {rows.length ? (
                        <AutoScrollViewport itemCount={rows.length}>
                          {rows.map((row) => (
                            <Box
                              key={row.id}
                              sx={{
                                px: 1.2,
                                py: compactBoard ? 0.75 : 0.95,
                                borderRadius: 1,
                                bgcolor: alpha(accentColor, theme.palette.mode === "dark" ? 0.16 : 0.1),
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 1fr) auto",
                                gap: 1,
                                alignItems: "center",
                                border: `1px solid ${alpha(accentColor, 0.16)}`,
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: { xs: 17, lg: 22 }, fontWeight: 950 }} noWrap>
                                  {row.department ? `${row.department}: ` : ""}{row.title}
                                </Typography>
                                {row.note && (
                                  <Typography sx={{ fontSize: { xs: 13, lg: 16 }, color: "text.secondary", fontWeight: 750 }} noWrap>
                                    {row.note}
                                  </Typography>
                                )}
                              </Box>
                              <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" sx={{ minWidth: 0 }}>
                                {row.orario && (
                                  <Chip
                                    size="small"
                                    label={row.orario}
                                    sx={{
                                      height: 30,
                                      fontSize: 15,
                                      fontWeight: 950,
                                      bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.18 : 0.78),
                                      border: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
                                    }}
                                  />
                                )}
                                <Typography sx={{ fontSize: { xs: 16, lg: 20 }, fontWeight: 950, color: "text.secondary" }}>
                                  x{Number(row.quantity || 1)}
                                </Typography>
                              </Stack>
                            </Box>
                          ))}
                        </AutoScrollViewport>
                      ) : (
                          <Box
                            sx={{
                              flex: 1,
                              minHeight: 54,
                              display: "grid",
                              placeItems: "center",
                              borderRadius: 1,
                              border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
                            }}
                          >
                            <Typography sx={{ color: "text.secondary", fontWeight: 850, textAlign: "center" }}>
                              {t("monitorEventi.noOperations", { defaultValue: "Nessun servizio operativo indicato" })}
                            </Typography>
                          </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })
          ) : (
            <Paper
              sx={{
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                textAlign: "center",
                p: 3,
                bgcolor: "background.paper",
              }}
            >
              <Box>
                <EventAvailableIcon color="disabled" sx={{ fontSize: 64, mb: 1 }} />
                <Typography sx={{ fontSize: { xs: 24, lg: 34 }, fontWeight: 950 }}>
                  {t("noEventsToday", { defaultValue: "Nessun evento programmato oggi." })}
                </Typography>
                <Typography sx={{ color: "text.secondary", mt: 0.5 }}>
                  {selectedTableId
                    ? t("monitorEventi.noEventsForRoom", { defaultValue: "Prova a selezionare tutte le sale." })
                    : t("monitorEventi.waitingRealtime", { defaultValue: "La vista si aggiorna automaticamente in tempo reale." })}
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>

        {hiddenRows > 0 && (
          <Box
            sx={{
              position: "fixed",
              left: "50%",
              bottom: 14,
              transform: "translateX(-50%)",
              px: 2,
              py: 0.75,
              borderRadius: 99,
              bgcolor: alpha(theme.palette.background.paper, 0.96),
              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              fontWeight: 900,
              boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.18)}`,
            }}
          >
            +{hiddenRows} {t("monitorEventi.moreEvents", { defaultValue: "eventi non visibili: filtra per sala" })}
          </Box>
        )}
      </Box>

      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          position: "fixed",
          top: 72,
          right: 14,
          width: 76,
          opacity: controlsVisible ? 1 : 0,
          pointerEvents: controlsVisible ? "auto" : "none",
          transform: controlsVisible ? "translateX(0)" : "translateX(22px)",
          transition: "opacity 160ms ease, transform 160ms ease",
          borderRadius: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.96),
          border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          boxShadow: `0 16px 36px ${alpha(theme.palette.common.black, 0.22)}`,
          p: 0.75,
          zIndex: 20,
        }}
      >
        <Stack spacing={0.5} alignItems="center">
          <MenuIcon color="action" />
          <IconButton aria-label="Aggiorna" onClick={loadData}>
            <RefreshIcon />
          </IconButton>
          <IconButton aria-label="Fullscreen" onClick={isFullscreen ? exitFullscreen : requestFullscreen}>
            {isFullscreen ? <CloseFullscreenIcon /> : <FullscreenIcon />}
          </IconButton>
          <Button
            variant="contained"
            color="primary"
            onClick={leaveMonitor}
            sx={{ minWidth: 0, width: 52, height: 44 }}
          >
            <LogoutIcon />
          </Button>
        </Stack>
      </Box>

      {!isFullscreen && (
        <Button
          onClick={(event) => {
            event.stopPropagation();
            requestFullscreen();
          }}
          startIcon={<FullscreenIcon />}
          variant="contained"
          sx={{
            position: "fixed",
            left: 18,
            bottom: 18,
            fontWeight: 900,
            boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.28)}`,
          }}
        >
          {t("monitorEventi.enterFullscreen", { defaultValue: "Schermo intero" })}
        </Button>
      )}

    </Box>
  );
}
